import {
  type IntakeSource,
  isIntakeSource
} from "../../components/cubeDataTable/utils/intakeUtils";
import type { MultiTableCellValueChanged } from "../../components/multiTableGrid/MultiTableGrid";
import type {
  EditConfig,
  GridData,
  MetricValues
} from "../../utils/summary/editPropagation";
import { processEdit } from "../../utils/summary/editPropagation";
import {
  columnarToGridData,
  padToRectangular,
  parseColumnId
} from "../../utils/summary/gridDataTransforms";
import {
  applyKpiChange,
  hasKpiPipeline
} from "../../utils/summary/kpiCalculations";
import {
  buildSalesKpiDerivations,
  INTAKE_COMPONENT_METRICS,
  OPENING_STOCK_COLUMNS,
  STOCK_KPI_FLOWS
} from "../../utils/summary/salesKpiDerivations";
import { INDEX_COL_SUFFIX } from "../../utils/summary/summaryPageHelpers";
import type { ForecastSummaryData } from "../useFetchForecastSummaryData";

/** Metrics allowed in the waterfall graph (matches Dash implementation). */
export const GRAPH_ALLOWED_METRICS = new Set([
  "GS1",
  "GS_DISCOUNT_COST",
  "GIS1",
  "GS1_COGS",
  "GS2",
  "GS2_MARGIN_VALUE",
  "GR2",
  "COGR",
  "GIR",
  "NS3",
  "NS3_COGS",
  "NS3_AUC",
  "NS3_MARGIN_VALUE",
  "NIS3",
  "INTAKE_UNITS_REPLENISHMENT",
  "INTAKE_UNITS_MAIN",
  "INTAKE_UNITS_INSEASON",
  "INTAKE_UNITS_CLEARANCE",
  "INTAKE_UNITS_TOTAL",
  "INTAKE_AT_COST_REPLENISHMENT",
  "INTAKE_AT_COST_MAIN",
  "INTAKE_AT_COST_INSEASON",
  "INTAKE_AT_COST_CLEARANCE",
  "INTAKE_AT_COST_TOTAL"
]);

/** Parsed result from a forecast summary API response. */
interface ParsedResponseData {
  metrics: string[];
  editConfig: EditConfig;
  idxData: Record<string, (number | null)[][]>;
  paddedData: Record<string, (number | null)[][]>;
  unifiedRowLabels: string[];
  realRowsPerTable: Set<string>[];
  gridData: GridData;
  /** One toggle-column cache per source; empty when the toggle is unavailable. */
  intakeCaches: SummaryIntakeCaches;
}

/** The toggle columns of one intake source, ``{column_id: [table][row]}``. */
export type SummaryIntakeCache = Record<string, (number | null)[][]>;

export type SummaryIntakeCaches = Partial<
  Record<IntakeSource, SummaryIntakeCache>
>;

/**
 * Parses a forecast summary API response into grid-ready data structures.
 * ``markdownSign`` (from the applied markdown_status) folds the context-dependent
 * planned-markdown correction into the EOP derivations; 0 (the default) is a
 * no-op.
 */
export function parseResponseData(
  response: ForecastSummaryData,
  markdownSign = 0
): ParsedResponseData {
  const responseMetrics = [
    ...new Set(Object.keys(response.data).map((k) => k.split("|")[0]))
  ].sort();

  const editConfig: EditConfig = {
    irreducibleMetrics: response.additive_metrics,
    kpiFlows: STOCK_KPI_FLOWS,
    endpointMetrics: response.endpoint_metrics,
    derivedMetrics: buildSalesKpiDerivations(markdownSign),
    ptdMutable: response.ptd_mutable
  };

  // Index columns are padded with the main ones: both render by unified-row
  // position.
  const {
    paddedData: padded,
    unifiedRowLabels: unified,
    realRowsPerTable: realRows
  } = padToRectangular(
    response.data,
    response.table_labels,
    response.row_labels_per_table
  );
  const paddedData: Record<string, (number | null)[][]> = {};
  const idxData: Record<string, (number | null)[][]> = {};
  for (const [k, v] of Object.entries(padded)) {
    if (k.endsWith(INDEX_COL_SUFFIX)) {
      idxData[k] = v;
    } else {
      paddedData[k] = v;
    }
  }

  // Grid state holds only the leaves the server names as client-owned;
  // everything else stays in the server payload (paddedData) or is derived at
  // render.
  const gridData = columnarToGridData(paddedData, response.additive_metrics);

  const intakeCaches: SummaryIntakeCaches = {};
  if (response.intake_data) {
    let openingToggles = false;
    for (const [source, columns] of Object.entries(response.intake_data)) {
      if (!isIntakeSource(source)) continue;
      intakeCaches[source] = padToRectangular(
        columns,
        response.table_labels,
        response.row_labels_per_table
      ).paddedData;
      // The opening toggles only when the backend sent an overlay opening
      // (future years — see OPENING_STOCK_COLUMNS).
      openingToggles ||= Object.keys(columns).some((k) =>
        OPENING_STOCK_COLUMNS.has(k)
      );
    }
    // The main source's cache is cut from the payload it rides in.
    const mainCache: SummaryIntakeCache = {};
    for (const [k, v] of Object.entries(paddedData)) {
      const metric = k.split("|")[0];
      if (
        INTAKE_COMPONENT_METRICS.has(metric) ||
        (openingToggles && OPENING_STOCK_COLUMNS.has(k))
      ) {
        mainCache[k] = v;
      }
    }
    intakeCaches[response.main_intake_source] = mainCache;
  }

  return {
    metrics: responseMetrics,
    editConfig,
    idxData,
    paddedData,
    unifiedRowLabels: unified,
    realRowsPerTable: realRows,
    gridData,
    intakeCaches
  };
}

/**
 * Applies a cell edit to the grid, handling both KPI pipeline and simple edits.
 *
 * Any edit whose metric has a KPI pipeline — irreducible (GS1, GIS1, …) or
 * derived (GS1_AUR, …) — is translated into irreducible deltas: the cascade
 * runs on `renderedCell` — the cell as currently rendered (irreducibles +
 * derived + anchors), so trigger-aware formulas can read AURs/AUCs — and only
 * the irreducible changes are committed to grid state via `processEdit`.
 * Editing an irreducible without its cascade holds its siblings fixed, which
 * silently rewrites the ratios between them (GS1 up, GS2 flat => the discount
 * absorbs the whole edit). Only pipeline-less metrics are set directly.
 */
export function applyCellEdit(
  prev: GridData,
  event: MultiTableCellValueChanged,
  unifiedRowIndex: number,
  editConfig: EditConfig,
  renderedCell: MetricValues
): GridData {
  const { metric, agg: editAgg } = parseColumnId(event.column);

  if (hasKpiPipeline(metric)) {
    const cellValues = { ...renderedCell };
    const oldValues = { ...cellValues };
    applyKpiChange(cellValues, metric, event.newValue);

    let result = prev;
    const irr = new Set(editConfig.irreducibleMetrics);
    for (const [key, newVal] of Object.entries(cellValues)) {
      if (oldValues[key] !== newVal && irr.has(key)) {
        result = processEdit({
          data: result,
          tableIndex: event.tableIndex,
          rowIndex: unifiedRowIndex,
          metric: key,
          agg: editAgg,
          newValue: newVal,
          config: editConfig
        });
      }
    }
    return result;
  }

  return processEdit({
    data: prev,
    tableIndex: event.tableIndex,
    rowIndex: unifiedRowIndex,
    metric,
    agg: editAgg,
    newValue: event.newValue,
    config: editConfig
  });
}

/**
 * Snapshots current intake component values from grid data into a cache
 * record, along with any opening stock columns in `serverColumns` (see
 * OPENING_STOCK_COLUMNS).
 */
export function snapshotIntakeCache(
  gridData: GridData,
  serverColumns: Record<string, (number | null)[][]>
): Record<string, (number | null)[][]> {
  const snapshot: Record<string, (number | null)[][]> = {};
  for (const metric of INTAKE_COMPONENT_METRICS) {
    for (const aggSuffix of ["|PTD", "|PTG"]) {
      const col = `${metric}${aggSuffix}`;
      const srcSlice = aggSuffix === "|PTD" ? gridData.ptd : gridData.ptg;
      snapshot[col] = srcSlice.map((table) =>
        table.map((row) => row[metric] ?? null)
      );
    }
  }
  for (const [col, values] of Object.entries(serverColumns)) {
    if (OPENING_STOCK_COLUMNS.has(col)) {
      snapshot[col] = values;
    }
  }
  return snapshot;
}
